import type {FeatureServiceBinder} from '@feature-hub/core';
import {getLanguage} from './get-language.js';

export interface LocaleServiceV3 {
  readonly getLocale: () => LocaleV3;
  readonly subscribe: (listener: LocaleListenerV3) => () => void;
}

export interface LocaleV3 {
  /**
   * The market ID identifies a market. It is used for fetching content from
   * AEM. The `countryCode`, `currency`, and `baseSiteId` can be inferred from
   * it.
   */
  readonly marketId: string;
  /**
   * IETF BCP 47 language tag
   */
  readonly languageTag: string;
  /**
   * ISO 639-1:2002
   */
  readonly language: string;
}

export type LocaleListenerV3 = (locale: LocaleV3) => void;

export interface CreateBinderV3Options {
  readonly locale: LocaleV3;
  readonly subscribe: (listener: LocaleListenerV3) => void;
}

export function createBinderV3(
  options: CreateBinderV3Options,
): FeatureServiceBinder<LocaleServiceV3> {
  const listeners = new Set<LocaleListenerV3>();
  const {languageTag, marketId} = options.locale;
  let locale = {
    language: getLanguage(languageTag),
    languageTag,
    marketId,
  };

  options.subscribe((newLocale) => {
    locale = newLocale;

    for (const listener of listeners) {
      listener(locale);
    }
  });

  return () => ({
    featureService: {
      getLocale: () => locale,

      subscribe: (listener) => {
        listeners.add(listener);

        return () => {
          listeners.delete(listener);
        };
      },
    },
  });
}
